import { useEffect, useMemo } from "react";
import _ from "lodash";

import { colors } from "theme/palette";
import { useCaching } from "utils/common";
import { EMPTY_LIST } from "utils/constants";

import Box from "components/Box";
import FixedTable from "components/FixedTable";
import Stack from "components/Stack";
import Text from "components/Text";

import PrintableMemoFieldTitle from "pages/Deal/PrintableMemo/components/PrintableMemoFieldTitle";

import { IPackageDetailsField } from "entities/Checklist/sdk";
import { getTableVerticalHeaders } from "entities/Field/components/FixedTableField/utils";

interface IPrintableFixedTableField {
  breakInside?: string;
  field: IPackageDetailsField;
  isNASection: boolean;
}

const PrintableFixedTableField = ({
  field,
  breakInside = "auto",
  isNASection,
}: IPrintableFixedTableField) => {
  const [value, setValue] = useCaching(
    _.get(field.values, "[0].column_value", EMPTY_LIST)
  );

  useEffect(() => {
    const columns =
      field.column_name?.split(",").map((column) => column.trim()) || [];

    if (_.isEmpty(value) && !_.isEmpty(field.rows) && !_.isEmpty(columns)) {
      setValue(
        getTableVerticalHeaders({
          firstColumn: columns[0],
          rows: field.rows,
        })
      );
    }
  }, [value, field, setValue]);

  const isNAField = useMemo(
    () => field.n_a_status || isNASection,
    [field, isNASection]
  );

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ borderBottom: `1px solid ${colors.blue40}` }}
      >
        <PrintableMemoFieldTitle field={field} disabled={isNAField} />

        {isNAField && (
          <Text sx={{ opacity: 0.5 }} data-testid="field-value-na">
            N/A
          </Text>
        )}
      </Stack>
      {!isNAField && (
        <Box
          sx={{
            breakInside: breakInside,
            paddingTop: 1,
            width: "100%",
          }}
          data-testid="dd-table-field-table"
        >
          <FixedTable
            headers={
              field.column_name?.split(",").map((column) => ({
                label: column.trim(),
                key: column.trim(),
              })) || []
            }
            rows={value}
            showEmptyRow={true}
            tableDataTestid={field.name}
          />
        </Box>
      )}
    </Box>
  );
};

export default PrintableFixedTableField;
